import { WithKit } from 'api/User/types';
import AddTestKitModal from 'components/AdminDashboard/components/ShippingStatusPage/components/AddTestKitModal/AddTestKitModal';
import EditKitModal from 'components/AdminDashboard/components/ShippingStatusPage/components/EditKitModal/EditKitModal';
import RenewShippingLabelsModal from 'components/AdminDashboard/components/ShippingStatusPage/components/RenewShippingLabelsModal/RenewShippingLabelsModal';
import ChangeTestKitReceivedModal from 'common/components/ChangeTestKitReceivedModal/ChangeTestKitReceivedModal';
import { useUserKits } from 'components/AdminDashboard/components/ShippingStatusPage/hooks';
import {
  getCurrentShipping,
  getDirectionLabel,
  getStatusLabel,
  isRenewingAllowed,
  isDeliveredFromUser,
} from 'components/AdminDashboard/components/ShippingStatusPage/helpers';
import { ShippingStatus } from 'api/Shipment/types';
import ShippingStatusPopover from 'components/AdminDashboard/components/ShippingStatusPage/components/ShippingStatusPopover/ShippingStatusPopover';
import OverviewStats from 'components/AdminDashboard/components/ShippingStatusPage/components/OverviewStats/OverviewStats';
import SearchUserInput from 'common/components/SearchUserInput/SearchUserInput';
import LoadingContainer from 'common/components/LoadingContainer/LoadingContainer';
import DataTable from 'common/components/DataTable/DataTable';
import { HeadCell } from 'common/types/MuiComponents';
import { formatDateTime } from 'common/helpers/dateTime';

import React, { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Checkbox, IconButton, Paper, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import { Refresh, Edit, CallMade, CallReceived } from '@mui/icons-material';

const ShippingStatusPage: React.FC = () => {
  const {
    requestUserKits,
    requestTransactionsOverview,
    userKits,
    transactionsOverview,
    pagination,
    setUserKits,
    isLoading,
    isOverviewLoading,
  } = useUserKits();

  const [ searchValue, setSearchValue ] = useState('');

  const [ shippingPopoverAnchorEl, setShippingPopoverAnchorEl ] = useState<HTMLElement | null>(null);
  const [ shippingPopoverStatus, setShippingPopoverStatus ] = useState<ShippingStatus | null>(null);

  const [ addTestKitModalOpened, setAddTestKitModalOpened ] = useState<boolean>(false);
  const [ editKitEntity, setEditKitEntity ] = useState<WithKit | null>(null);
  const [ renewKitEntity, setRenewKitEntity ] = useState<WithKit | null>(null);
  const [ changeTestKitReceivedUserId, setChangeTestKitReceivedUserId ] = useState<string | null>(null);

  const headCells = useMemo((): HeadCell[] => [
    {
      id: 'requestDate',
      numeric: false,
      label: 'Request date',
    },
    {
      id: 'userId',
      numeric: false,
      label: 'User id',
    },
    {
      id: 'user',
      numeric: false,
      label: 'User',
      button: <SearchUserInput onChange={e => setSearchValue(e.target.value)} />,
    },
    {
      id: 'shippingStatus',
      numeric: true,
      label: 'Shipping Status',
    },
    {
      id: 'kitId',
      numeric: true,
      label: 'Kit id',
    },
    {
      id: 'testKitReceived',
      numeric: false,
      label: 'Test Kit Received',
    },
  ], []);

  const handleKitAdded = (withKit: WithKit) => {
    setUserKits([
      withKit,
      ...userKits.slice(0, userKits.length - 1),
    ]);

    setAddTestKitModalOpened(false);
  };

  const handleKitChanged = (id: string, kitId: string) => {
    const kitIndex = userKits.findIndex(item => item.id === id);

    setUserKits([
      ...userKits.slice(0, kitIndex),
      { ...userKits[kitIndex], kitId },
      ...userKits.slice(kitIndex + 1, userKits.length),
    ]);

    setEditKitEntity(null);
  };

  const handleLabelsChanged = (oldWithKitId: string, withKit: WithKit) => {
    const kitIndex = userKits.findIndex(item => item.id === oldWithKitId);

    setUserKits([
      ...userKits.slice(0, kitIndex),
      { ...withKit },
      ...userKits.slice(kitIndex + 1, userKits.length),
    ]);

    setRenewKitEntity(null);
  };

  const handleTestKitReceivedChanged = (id: string, kitReceived: boolean) => {
    const kitIndex = userKits.findIndex(item => item.id === id);

    setUserKits([
      ...userKits.slice(0, kitIndex),
      { ...userKits[kitIndex], kitReceived },
      ...userKits.slice(kitIndex + 1, userKits.length),
    ]);

    setChangeTestKitReceivedUserId(null);
  };

  const onEdit = useCallback((index: number) => {
    setEditKitEntity(userKits[index]);
  }, [ userKits, setEditKitEntity ]);

  const onRenew = useCallback((index: number) => {
    setRenewKitEntity(userKits[index]);
  }, [ userKits, setRenewKitEntity ]);

  const onChangeTestKitReceived = useCallback((index: number) => {
    setChangeTestKitReceivedUserId(userKits[index].id);
  }, [ userKits, setChangeTestKitReceivedUserId ]);

  useEffect(() => {
    requestTransactionsOverview();
  }, [ requestTransactionsOverview ]);

  const handleShippingPopoverShow = (event: React.MouseEvent<HTMLElement>, shippingStatus: ShippingStatus | null) => {
    setShippingPopoverAnchorEl(event.currentTarget);
    setShippingPopoverStatus(shippingStatus);
  };
  const handleShippingPopoverClose = () => {
    setShippingPopoverAnchorEl(null);
    setShippingPopoverStatus(null);
  };

  const renderRows = useCallback(() => userKits
    .map((row, index: number) => {
      const currentShipping = getCurrentShipping(row.directTransaction, row.returnTransaction);
      const isReceivedFromUser = isDeliveredFromUser(currentShipping);
      const testKitReceived = isReceivedFromUser || !!row.kitReceived;

      return (
        <TableRow hover tabIndex={-1} key={row.id}>
          <TableCell sx={{ whiteSpace: 'nowrap' }}>
            {formatDateTime(row.directTransaction?.transactionObject.objectCreated || row.returnTransaction?.transactionObject.objectCreated)}
          </TableCell>
          <TableCell>
            <Typography variant="textSRegular">
              {row.user.id}
            </Typography>
          </TableCell>
          <TableCell
            component="th"
            scope="row"
            padding="normal"
          >
            <Typography variant="textSRegular" component="div">
              {`${row.user.firstName} ${row.user.lastName}`}
            </Typography>
            <Typography variant="textSRegular" component="div">
              {row.user.username}
            </Typography>
          </TableCell>
          <TableCell align="center">
            <Typography
              variant="textSRegular"
              component="div"
              sx={{
                textDecorationStyle: 'dashed',
                textDecorationLine: 'underline',
                textDecorationColor: 'gray',
                textUnderlineOffset: '2px',
                cursor: 'pointer',
              }}
              onClick={(e: MouseEvent<HTMLElement>) => handleShippingPopoverShow(e, currentShipping.status)}
              data-testid="label-status-hint"
            >
              {getStatusLabel(currentShipping.status)}
            </Typography>
            <Typography variant="textSRegular" component="div">
              {getDirectionLabel(currentShipping.direction)}
            </Typography>
          </TableCell>
          <TableCell align="right">
            <Typography
              component="span"
              sx={{
                fontSize: '14px',
                fontFamily: 'Red Hat Mono',
                whiteSpace: 'nowrap',
              }}
            >
              {row.kitId}
            </Typography>
            <Tooltip title="Edit test kit id">
              <IconButton color="primary" size="small" onClick={() => onEdit(index)} data-testid="button-edit">
                <Edit />
              </IconButton>
            </Tooltip>
            {row.directTransaction && (
              <Tooltip title="Open direct shipping label">
                <IconButton size="small" color="primary" onClick={() => window.open(row.directTransaction!.transactionObject.labelURL)}>
                  <CallMade />
                </IconButton>
              </Tooltip>
            )}
            {row.returnTransaction && (
              <Tooltip title="Open return shipping label">
                <IconButton color="primary" size="small" onClick={() => window.open(row.returnTransaction!.transactionObject.labelURL)}>
                  <CallReceived />
                </IconButton>
              </Tooltip>
            )}
            {isRenewingAllowed(currentShipping) && (
              <Tooltip title="Renew labels">
                <IconButton color="primary" size="small" onClick={() => onRenew(index)} data-testid="button-renew">
                  <Refresh />
                </IconButton>
              </Tooltip>
            )}
          </TableCell>
          <TableCell>
            <Checkbox
              disabled={isReceivedFromUser}
              onChange={() => onChangeTestKitReceived(index)}
              checked={testKitReceived}
              value={testKitReceived}
            />
          </TableCell>
        </TableRow>
      );
    }), [ userKits, onEdit, onRenew, onChangeTestKitReceived ]);

  return (
    <LoadingContainer isLoading={isLoading || isOverviewLoading}>

      <OverviewStats overview={transactionsOverview} />

      <Box display="flex" justifyContent="flex-end" marginBottom={2}>

        <Button
          sx={{
            backgroundColor: 'white',
            width: '150px',
            textTransform: 'none',
          }}
          variant="text"
          onClick={() => setAddTestKitModalOpened(true)}
        >
          Add Test Kit
        </Button>

      </Box>

      <Box data-testid="shipping-status-page">

        <Paper sx={{ maxWidth: '100%' }}>
          <DataTable
            pagination={pagination}
            headCells={headCells}
            renderRows={renderRows}
            searchValue={searchValue}
            updateDataCallback={requestUserKits}
          />
        </Paper>

        {addTestKitModalOpened && <AddTestKitModal onCancel={() => setAddTestKitModalOpened(false)} onSave={handleKitAdded} />}

        {editKitEntity && <EditKitModal onCancel={() => setEditKitEntity(null)} onSave={handleKitChanged} withKit={editKitEntity} />}

        {renewKitEntity && (
          <RenewShippingLabelsModal onCancel={() => setRenewKitEntity(null)} onSave={handleLabelsChanged} withKit={renewKitEntity} />
        )}

        {shippingPopoverAnchorEl && (
          <ShippingStatusPopover anchorEl={shippingPopoverAnchorEl} shippingPopoverStatus={shippingPopoverStatus} onClose={handleShippingPopoverClose} />
        )}

        {changeTestKitReceivedUserId && (
          <ChangeTestKitReceivedModal
            onCancel={() => setChangeTestKitReceivedUserId(null)}
            onSave={handleTestKitReceivedChanged}
            id={changeTestKitReceivedUserId}
          />
        )}
      </Box>

    </LoadingContainer>
  );
};

export default ShippingStatusPage;
